/**
 * @file customer-ledger.tsx
 * @module app/modules/customers/customer-ledger
 * @description Displays the customer ledger with searchable, paginated account records and
 * supports exporting the full filtered ledger to an Excel workbook.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";
import { Button, Card, Col, Modal, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { KTSVG } from "../../../_admin/helpers";
import APICallService from "../../../api/apiCallService";
import { buyer } from "../../../api/apiEndPoints";
import Loader from "../../../Global/loader";
import Pagination from "../../../Global/pagination";
import {
  CustomerLedger,
  PAGE_LIMIT,
  View,
  AllCustomers,
  ORDER_PAGE_LIMIT,
} from "../../../utils/constants";
import b1 from "../../../_admin/assets/media/product/default.svg";
import Method from "../../../utils/methods";
import { useAuth } from "../auth";
import { IGetCustomers } from "../../../types/responseIndex";
import clsx from "clsx";
import OrderPagination from "../../../Global/order-pagination";
import PermissionModal from "../../modals/permission";
import * as XLSX from "xlsx";
import { useWarehouse } from "../../context/WarehouseContext";
import { useDebounce } from "../../../utils/useDebounce";
import { useNavigationHistory } from "../../../utils/useNavigationHistory";
import ImageWithFallback from "../_common/components/image-with-fallback";

const ExcelExportModal = ({
  show,
  onHide,
  searchTerm,
  sortKey,
  sortOrder,
}: {
  show: boolean;
  onHide: () => void;
  searchTerm: string;
  sortKey: string;
  sortOrder: number;
}) => {
  const { selectedWarehouse } = useWarehouse();
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [processedRecords, setProcessedRecords] = useState(0);
  const [error, setError] = useState("");

  const CHUNK_SIZE = 100;

  const handleExport = async () => {
    setIsExporting(true);
    setError("");
    setProgress(0);
    setCurrentStep("Initializing export...");

    try {
      // First, get total count using existing API structure
      setCurrentStep("Calculating total records...");

      let countParams: any = {
        pageNo: 1,
        sortKey: sortKey,
        sortOrder: sortOrder,
        searchTerm: searchTerm,
        needCount: true,
        state: 1,
      };

      let countApiService = new APICallService(
        buyer.listBuyer,
        countParams,
        "",
        "",
        "",
        "",
        CustomerLedger
      );
      let countResponse = await countApiService.callAPI();

      if (!countResponse || !countResponse?.total) {
        setError("Failed to get total count");
        return;
      }

      const total = countResponse?.total;
      setTotalRecords(total);

      if (total === 0) {
        setError("No records found to export");
        return;
      }

      // Warn user for large datasets
      if (total > 5000) {
        setCurrentStep(
          `Large dataset detected (${total?.toLocaleString()} records). This may take several minutes...`
        );
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      // Prepare Excel workbook
      const workbook = XLSX.utils.book_new();
      const worksheetData = [];

      // Add headers
      const headers = [
        "Customer Name",
        "Business Name",
        "Total Orders",
        "Paid Amount (TSh)",
        "Remaining Amount (TSh)",
        "Order Date",
        "Order ID",
        "Order Total (TSh)",
        "Paid Amount (TSh)",
        "Amount Remaining (TSh)",
      ];
      worksheetData?.push(headers);

      // Process in chunks
      const totalChunks = Math.ceil(total / CHUNK_SIZE);
      let processedRecords = 0;

      for (let page = 1; page <= totalChunks; page++) {
        setCurrentStep(
          `Processing records ${processedRecords + 1} to ${Math.min(
            processedRecords + CHUNK_SIZE,
            total
          )} of ${total}...`
        );

        try {
          // Fetch customer chunk
          let chunkParams: any = {
            pageNo: page,
            limit: CHUNK_SIZE,
            sortKey: sortKey,
            sortOrder: sortOrder,
            searchTerm: searchTerm,
            needCount: false,
            state: 1,
          };

          let chunkApiService = new APICallService(
            buyer.listBuyer,
            chunkParams,
            "",
            "",
            "",
            "",
            CustomerLedger
          );
          let chunkResponse = await chunkApiService.callAPI();

          if (!chunkResponse || !chunkResponse.records) {
            throw new Error(`Failed to fetch chunk ${page}`);
          }

          // Process each customer in the chunk
          for (const customer of chunkResponse.records) {
            console.log(customer, "090909");
            // Add customer summary row (main row) with defensive programming
            const customerSummaryRow = [
              customer?.name || customer?.business?.name || "Unknown Customer",
              customer?.business?.name || "",
              customer?.analytics[0]?.totalOrders ||
                customer?.analytics[0]?.totalOrderValue ||
                0,
              customer?.analytics[0]?.totalAmount || 0,
              customer?.analytics[0]?.remainingCharge ||
                customer?.analytics[0]?.remainingAmount ||
                0,
              "", // Empty for order date in summary row
              "", // Empty for order ID in summary row
              "", // Empty for order total in summary row
              "", // Empty for paid amount in summary row
              "", // Empty for order remaining in summary row
            ];
            worksheetData.push(customerSummaryRow);

            // Fetch order details for this customer
            try {
              setCurrentStep(
                `Fetching orders for ${
                  customer?.name || customer?.business?.name || "Customer"
                }...`
              );

              let orderParams = {
                customer: customer?._id,
                status: 1,
              };

              let orderApiService = new APICallService(
                buyer.receivablePayouts,
                orderParams,
                "",
                "",
                "",
                "",
                CustomerLedger,
                selectedWarehouse?.value
              );
              let orderResponse = await orderApiService.callAPI();

              // Debug logging
              console.log(
                `Orders for customer ${customer?._id}:`,
                orderResponse
              );

              if (
                orderResponse &&
                orderResponse?.records &&
                orderResponse?.records?.length > 0
              ) {
                orderResponse?.records?.forEach((order: any) => {
                  // Debug logging for order structure
                  console.log(`Processing order:`, order);

                  // Add order detail row (sub-row with indentation effect)
                  const orderDetailRow = [
                    "", // Empty customer name for sub-rows
                    "", // Empty business name for sub-rows
                    "", // Empty total orders for sub-rows
                    "", // Empty total amount for sub-rows
                    "", // Empty remaining amount for sub-rows
                    order?._createdAt
                      ? Method.convertDateToDDMMYYYY(order?._createdAt)
                      : order.createdAt
                      ? Method.convertDateToDDMMYYYY(order?.createdAt)
                      : "",
                    order?.refKey || order?.orderId || order?._id || "N/A",
                    order?.payment?.totalCharge ||
                      order?.totalAmount ||
                      order?.amount ||
                      0,
                    order?.payment?.receivedCharge ||
                      order?.paidAmount ||
                      order?.receivedAmount ||
                      0,
                    order?.payment?.remainingCharge ||
                      order?.remainingAmount ||
                      order?.balance ||
                      0,
                  ];
                  worksheetData.push(orderDetailRow);
                });
              } else {
                // Customer with no orders - add empty sub-row
                const emptyOrderRow = ["", "", "", "", "", "", "", "", "", ""];
                worksheetData.push(emptyOrderRow);
              }

              // Small delay to prevent overwhelming the server
              await new Promise((resolve) => setTimeout(resolve, 100));
            } catch (orderError) {
              console.error(
                `Error fetching orders for customer ${customer._id}:`,
                orderError
              );
              // Add empty order row if there's an error
              const emptyOrderRow = ["", "", "", "", "", "", "", "", "", ""];
              worksheetData.push(emptyOrderRow);
            }
          }

          processedRecords += chunkResponse?.records?.length;
          setProcessedRecords(processedRecords);
          setProgress((processedRecords / total) * 100);

          // Small delay to prevent overwhelming the server
          await new Promise((resolve) => setTimeout(resolve, 200));
        } catch (chunkError) {
          console.error(`Error processing chunk ${page}:`, chunkError);
          setError(
            `Failed to process chunk ${page}. Some data may be missing.`
          );
          // Continue with next chunk instead of failing completely
        }
      }

      // Create and download Excel file
      setCurrentStep("Generating Excel file...");
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Set column widths
      worksheet["!cols"] = [
        { width: 25 }, // Customer Name
        { width: 25 }, // Business Name
        { width: 15 }, // Total Orders
        { width: 20 }, // Total Amount
        { width: 20 }, // Remaining Amount
        { width: 15 }, // Order Date
        { width: 20 }, // Order ID
        { width: 15 }, // Order Total
        { width: 15 }, // Paid Amount
        { width: 18 }, // Order Remaining
      ];

      // Add styling for hierarchical structure
      const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");

      // Style header row
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!worksheet[cellRef]) continue;
        worksheet[cellRef].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center" },
        };
      }

      // Style customer summary rows (rows with customer data)
      for (let row = 1; row <= range.e.r; row++) {
        const customerNameCell = XLSX.utils.encode_cell({ r: row, c: 0 });
        if (
          worksheet[customerNameCell] &&
          worksheet[customerNameCell].v !== ""
        ) {
          // This is a customer summary row
          for (let col = 0; col <= 4; col++) {
            const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
            if (worksheet[cellRef]) {
              worksheet[cellRef].s = {
                font: { bold: true },
                fill: { fgColor: { rgb: "E7E6E6" } },
              };
            }
          }
        }
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, "Customer Ledger");

      setCurrentStep("Downloading file...");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `customer-ledger-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setCurrentStep("Export completed successfully!");
      setTimeout(() => {
        onHide();
      }, 2000);
    } catch (error) {
      console.error("Export error:", error);
      setError("Export failed: " + (error as Error).message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    if (isExporting) {
      if (
        window.confirm(
          "Export is in progress. Are you sure you want to cancel?"
        )
      ) {
        onHide();
      }
    } else {
      onHide();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={isExporting ? "static" : true}
      keyboard={!isExporting}
      centered
      size="lg"
    >
      <Modal.Header closeButton={!isExporting}>
        <Modal.Title>Export Customer Ledger</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!isExporting && !error && (
          <div>
            <div className="alert alert-info">
              <h6>
                <strong>Export Information:</strong>
              </h6>
              <ul className="mb-2">
                <li>
                  This will export all customer ledger data to Excel format
                </li>
                <li>Large datasets may take several minutes to process</li>
                <li>Please keep this window open during export</li>
                <li>The file will automatically download when ready</li>
              </ul>
            </div>

            <div className="alert alert-warning">
              <strong>Note:</strong> For datasets with 10,000+ records, the
              export may take 5-10 minutes. Please be patient and do not close
              this window.
            </div>
          </div>
        )}

        {isExporting && (
          <div>
            <div className="d-flex align-items-center mb-3">
              <Spinner animation="border" size="sm" className="me-3" />
              <strong>Exporting Data...</strong>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between mb-2">
                <span>Progress:</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="progress">
                <div
                  className={`progress-bar ${
                    progress === 100 ? "bg-success" : "bg-primary"
                  }`}
                  role="progressbar"
                  style={{ width: `${progress}%` }}
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
            </div>

            {totalRecords > 0 && (
              <div className="mb-3">
                <small className="text-muted">
                  Processed: {processedRecords.toLocaleString()} of{" "}
                  {totalRecords.toLocaleString()} records
                </small>
              </div>
            )}

            <div className="mb-2">
              <small>{currentStep}</small>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <strong>Error:</strong> {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!isExporting && (
          <>
            <Button className="uw-btn uw-btn-light" variant="secondary" data-analytics-event="customers_customer_ledger_export_cancel_clicked" onClick={handleClose}>
              Cancel
            </Button>
            <Button className="uw-btn uw-btn-primary" variant="primary" data-analytics-event="customers_customer_ledger_export_start_clicked" onClick={handleExport}>
              Start Export
            </Button>
          </>
        )}

        {isExporting && (
          <Button className="uw-btn uw-btn-light" variant="outline-secondary" data-analytics-event="customers_customer_ledger_export_cancel_running_clicked" onClick={handleClose}>
            Cancel Export
          </Button>
        )}

        {error && (
          <Button className="uw-btn uw-btn-primary"
            variant="primary"
            data-analytics-event="customers_customer_ledger_export_try_again_clicked"
            onClick={() => {
              setError("");
              setProgress(0);
              setCurrentStep("");
            }}
          >
            Try Again
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
const CustomerLedgerPage = () => {
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const [fetchLoading, setFetchLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(ORDER_PAGE_LIMIT);
  const [customers, setCustomers] = useState<IGetCustomers | any>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [customerLedgerData, setCustomerLedgerData] = useState<any>({});
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [sortKey, setSortKey] = useState("totalAmount");
  const [sortOrder, setSortOrder] = useState(-1);
  const [showExportModal, setShowExportModal] = useState(false);
  const [loadingLedger, setLoadingLedger] = useState<any>({});

  useEffect(() => {
    (async () => {
      setFetchLoading(true);
      // if (!Method.hasModulePermission(CustomerLedger, currentUser)) {
      //   return window.history.back();
      // }
      await fetchCustomerData(page, pageLimit, searchTerm, sortKey, sortOrder);
      setFetchLoading(false);
    })();
  }, [selectedWarehouse?.value]);

  const fetchCustomerData = async (
    pageNo: number,
    limit: number,
    search: string,
    sortField: string = sortKey,
    sortDirection: number = sortOrder
  ) => {
    setLoading(true);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: sortField,
      sortOrder: sortDirection,
      searchTerm: search,
      needCount: true,
      state: 1, // Only registered customers
    };

    let apiService = new APICallService(
      buyer.listBuyer,
      params,
      "",
      "",
      "",
      "",
      CustomerLedger,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response) {
      if (response?.total) {
        setTotalRecords(response?.total);
      } else {
        setTotalRecords(0);
      }
      setCustomers(response?.records || []);
    } else {
      setCustomers([]);
      setTotalRecords(0);
    }
    setLoading(false);
  };

  const handleCurrentPage = async (val: number) => {
    setLoading(true);
    setPage(val);
    await fetchCustomerData(val, pageLimit, searchTerm, sortKey, sortOrder);
    setLoading(false);
  };

  const handleNextPage = async (val: number) => {
    setLoading(true);
    setPage(val + 1);
    await fetchCustomerData(val + 1, pageLimit, searchTerm, sortKey, sortOrder);
    setLoading(false);
  };

  const handlePreviousPage = async (val: number) => {
    setLoading(true);
    setPage(val - 1);
    await fetchCustomerData(val - 1, pageLimit, searchTerm, sortKey, sortOrder);
    setLoading(false);
  };

  const handlePageLimit = async (event: any) => {
    setLoading(true);
    setPage(1);
    await setPageLimit(parseInt(event.target.value));
    await fetchCustomerData(
      1,
      event.target.value,
      searchTerm,
      sortKey,
      sortOrder
    );
    setLoading(false);
  };

const debounce = useDebounce(fetchCustomerData,500)

  const handleSearch = async (value: string) => {
    setSearchTerm(value);
    setPage(1);
    // setLoading(true);
    await debounce(1, pageLimit, value, sortKey, sortOrder);
    // setLoading(false);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "1":
        return "Cash";
      case "2":
        return "Tigo Pesa";
      case "3":
        return "MPesa";
      case "4":
        return "Card";
      case "5":
        return "DTB Bank";
      case "6":
        return "CRDB Bank";
      case "7":
        return "NMB Bank";
      case "8":
        return "Stanbic Bank";
      case "9":
        return "Airtel Money";
      case "10":
        return "Halo Pesa";
      default:
        return "Unknown";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "1":
        return "Pending";
      case "2":
        return "Completed";
      case "3":
        return "Cancelled";
      default:
        return "Unknown";
    }
  };
  const handleSort = async (field: string) => {
    let newSortOrder = 1; 
    if (sortKey === field) {
      newSortOrder = sortOrder === 1 ? -1 : 1;
    }

    setSortKey(field);
    setSortOrder(newSortOrder);
    setPage(1); 

    setExpandedDriver([]);

    await fetchCustomerData(1, pageLimit, searchTerm, field, newSortOrder);
  };
  const fetchCustomerLedger = async (customerId: string) => {
    setLoadingLedger({ ...loadingLedger, [customerId]: true });

    let params: any = {
      customer: customerId,
      status: 1,
    };

    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }

    let apiService = new APICallService(
      buyer.receivablePayouts,
      params,
      "",
      "",
      "",
      "",
      CustomerLedger,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response) {
      setCustomerLedgerData({
        ...customerLedgerData,
        [customerId]: response?.records,
      });
    }
    setLoadingLedger({ ...loadingLedger, [customerId]: false });
  };
  const getSortIcon = (field: string) => {
    if (sortKey !== field) {
      return (
        <KTSVG
          path="/media/icons/duotune/arrows/arr003.svg"
          className="svg-icon-2 svg-icon-gray-400 ms-1"
        />
      );
    }

    return sortOrder === 1 ? (
      <KTSVG
        path="/media/icons/duotune/arrows/arr004.svg"
        className="svg-icon-2 svg-icon-gray-400 ms-1"
      />
    ) : (
      <KTSVG
        path="/media/icons/duotune/arrows/arr003.svg"
        className="svg-icon-2 svg-icon-gray-400 ms-1"
      />
    );
  };

  const [expandedDriver, setExpandedDriver] = useState(
    new Array(customers?.length).fill(false)
  );

  console.log(expandedDriver, "909090");

  const toggleDriverDetails = (index: number) => {
    const newExpandedDriver = [...expandedDriver];
    newExpandedDriver[index] = !newExpandedDriver[index];
    setExpandedDriver(newExpandedDriver);
  };
  return (
    <>
      <div className="p-5">
        <Row className="align-items-center mb-5">
          <Col sm>
            <h1 className="fs-22 fw-bolder mb-0">Customer Ledger</h1>
          </Col>
          <Col xs="auto">
            <Button
              variant="success"
              data-analytics-event="customers_customer_ledger_export_excel_clicked"
              onClick={() => setShowExportModal(true)}
              className="uw-btn uw-btn-success me-3"
            >
              <KTSVG
                path="/media/icons/duotune/files/fil003.svg"
                className="svg-icon-2 me-2"
              />
              Export to Excel
            </Button>
          </Col>
        </Row>
        {showPermissionModal && (
          <PermissionModal
            show={showPermissionModal}
            onHide={() => setShowPermissionModal(false)}
            moduleName={"orders & delivery"}
          />
        )}
        {/* Search Bar */}
        <Card className="border bg-light mb-4">
          <Card.Body className="px-7">
            <Row className="align-items-center">
              <Col md={6}>
                <div>
                  <div className="d-flex align-items-center min-w-md-250px w-100 w-fit-content position-relative me-lg-4">
                    <KTSVG
                      path="/media/icons/duotune/general/gen021.svg"
                      className="svg-icon-3 position-absolute ps-5"
                    />
                    <input
                      type="text"
                      id="kt_filter_search"
                      value={searchTerm}
                      className="form-control form-control-custom bg-white min-h-30px ps-13 text-dark"
                      placeholder="Search by customer name,business name..."
                      onChange={(event: any) => {
                        handleSearch(event.target.value);
                      }}
                    />
                  </div>
                </div>
              </Col>
              <Col>
                <ExcelExportModal
                  show={showExportModal}
                  onHide={() => setShowExportModal(false)}
                  searchTerm={searchTerm}
                  sortKey={sortKey}
                  sortOrder={sortOrder}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="table-responsive border rounded">
          {!fetchLoading && customers?.length === 0 ? (
            <div className="text-center py-7">
              <div className="fs-16 text-muted">No customer found</div>
            </div>
          ) : (
            <table
              className="table table-rounded table-row-bordered align-middle gs-7 gy-4 mb-0"
              style={{ tableLayout: "auto" }}
            >
              <thead>
                <tr className="fs-16 fw-600 h-65px align-middle">
                  <th className="min-w-150px">All Customers</th>
                  <th className="min-w-150px" style={{ whiteSpace: "nowrap" }}>
                    Total Orders{" "}
                    <span onClick={() => handleSort("totalOrders")}>
                      {getSortIcon("totalOrders")}
                    </span>
                  </th>
                  <th className="min-w-150px" style={{ whiteSpace: "nowrap" }}>
                    Paid Amount{" "}
                    <span onClick={() => handleSort("totalAmount")}>
                      {getSortIcon("totalAmount")}
                    </span>
                  </th>
                  <th className="min-w-150px" style={{ whiteSpace: "nowrap" }}>
                    Remaining Amount{" "}
                    <span onClick={() => handleSort("remainingCharge")}>
                      {getSortIcon("remainingCharge")}
                    </span>
                  </th>

                  <th className="text-end"></th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: "#f9f9f9" }}>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-7">
                      <Spinner animation="border" variant="primary" />
                    </td>
                  </tr>
                ) : (
                  customers?.map((customerVal: any, primaryIndex: any) => (
                    <React.Fragment key={"kt_accordion_" + customerVal?._id}>
                      <tr
                        className={
                          expandedDriver[primaryIndex] && "text-primary"
                        }
                        onClick={() => {
                          toggleDriverDetails(primaryIndex);
                          if (!customerLedgerData[customerVal?._id]) {
                            fetchCustomerLedger(customerVal?._id);
                          }
                        }}
                      >
                        <td>
                          <span className="symbol symbol-35px border bg-body border-r8px me-3">
                            <ImageWithFallback
                              className="img-fluid"
                              src={customerVal?.business?.image || b1}
                              alt=""
                              fallbackSrc={b1}
                              noDefaultSize
                            />
                          </span>
                          <span className="fs-15 fw-600">
                            {customerVal?.business?.name || customerVal?.name}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-600">
                            {customerVal?.analytics?.length &&
                            customerVal?.analytics[0]?.totalOrders !== undefined
                              ? customerVal?.analytics[0]?.totalOrders > 1
                                ? customerVal?.analytics[0]?.totalOrders +
                                  " orders"
                                : customerVal?.analytics[0]?.totalOrders +
                                  " order"
                              : "0 order"}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-600">
                            {customerVal?.analytics.length &&
                            customerVal?.analytics[0]?.totalAmount &&
                            customerVal?.analytics[0]?.totalAmount !== undefined
                              ? "TSh " +
                                Method.getGeneralizedAmount(
                                  customerVal?.analytics[0]?.totalAmount
                                )
                              : "TSh " + 0}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-600">
                            {customerVal?.analytics.length &&
                            customerVal?.analytics[0]?.remainingCharge &&
                            customerVal?.analytics[0]?.remainingCharge !==
                              undefined
                              ? "TSh " +
                                Method.getGeneralizedAmount(
                                  customerVal?.analytics[0]?.remainingCharge
                                )
                              : "TSh " + 0}
                          </span>
                        </td>

                        <td className="text-end">
                          <Button
                            variant="link"
                            className="p-0 me-2 text-muted"
                            data-analytics-event="customers_customer_ledger_expand_row_clicked"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDriverDetails(primaryIndex);
                              if (!customerLedgerData[customerVal?._id]) {
                                fetchCustomerLedger(customerVal?._id);
                              }
                            }}
                          >
                            <i
                              className={`fas fa-chevron-${
                                expandedDriver[primaryIndex] ? "up" : "down"
                              }`}
                            ></i>
                          </Button>
                        </td>
                      </tr>

                      {/* Expanded Row for Driver Details (Level 2) */}
                      {expandedDriver[primaryIndex] && (
                        <tr>
                          <td colSpan={8}>
                            <div className="table-responsive border rounded">
                              {!loadingLedger[customerVal?._id] &&
                              customerLedgerData[customerVal?._id]?.length ===
                                0 ? (
                                <div className="text-center py-7">
                                  <div className="fs-16 text-muted">
                                    No data found
                                  </div>
                                </div>
                              ) : (
                                <table className="table table-rounded table-row-bordered align-middle gs-7 gy-4 mb-0">
                                  <thead className="bg-white">
                                    <tr className="fs-16 fw-600 h-65px align-middle">
                                      <th className="min-w-150px">
                                        Order Date
                                      </th>
                                      <th className="min-w-150px">Order ID</th>
                                      <th className="min-w-150px">
                                        Total Amount
                                      </th>
                                      <th className="min-w-150px">
                                        Paid Amount
                                      </th>
                                      <th className="w-350px">
                                        Remaining Amount
                                      </th>
                                      <th className="text-end"></th>
                                    </tr>
                                  </thead>
                                  {loadingLedger[customerVal?._id] ? (
                                    <div className="d-flex justify-content-center m-4 align-items-center">
                                      <Loader
                                        loading={loadingLedger[customerVal?._id]}
                                      />
                                    </div>
                                  ) : (
                                    <tbody>
                                      {customerLedgerData[customerVal?._id]?.map(
                                        (ledger: any, ledgerIndex: number) => (
                                          <React.Fragment key={ledger?._id}>
                                            <tr>
                                              <td>
                                                <span className="text-dark fw-bold fs-6">
                                                  {Method.convertDateToDDMMYYYY(
                                                    ledger?._createdAt
                                                  )}
                                                </span>
                                              </td>
                                              <td>
                                                <span className="text-dark fw-bold fs-6">
                                                  {ledger?.refKey || "N/A"}
                                                </span>
                                              </td>
                                              <td>
                                                <span className="text-dark fw-bold fs-6">
                                                  {"TSh " +
                                                    Method.getGeneralizedAmount(
                                                      ledger?.payment?.totalCharge
                                                    )}
                                                </span>
                                              </td>
                                              <td>
                                                <span className="text-dark fw-bold fs-6">
                                                  {"TSh " +
                                                    Method.getGeneralizedAmount(
                                                      ledger?.payment
                                                        ?.receivedCharge
                                                    )}
                                                </span>
                                              </td>
                                              <td>
                                                <span className=" fw-bold fs-6 text-danger">
                                                  {"TSh " +
                                                    Method.getGeneralizedAmount(
                                                      ledger?.payment?.remainingCharge
                                                    )}
                                                </span>
                                              </td>
                                              <td>
                                                {Method.hasPermission(
                                                  CustomerLedger,
                                                  View,
                                                  currentUser
                                                ) &&
                                                Method.hasPermission(
                                                  CustomerLedger,
                                                  View,
                                                  currentUser
                                                ) ? (
                                                  <Button
                                                    type="button"
                                                    variant="primary"
                                                    className="uw-btn uw-btn-primary uw-btn-sm btn-table"
                                                    data-analytics-event="customers_customer_ledger_view_details_clicked"
                                                    onClick={() => {
                                                      navigateTo(
                                                        "/orders/order-details",
                                                        {
                                                          state: {
                                                            id: ledger?._id,
                                                            moduleName:
                                                              CustomerLedger,
                                                          },
                                                        }
                                                      );
                                                    }}
                                                  >
                                                    View Details
                                                  </Button>
                                                ) : (
                                                  <></>
                                                )}
                                                {Method.hasPermission(
                                                  CustomerLedger,
                                                  View,
                                                  currentUser
                                                ) &&
                                                !Method.hasPermission(
                                                  CustomerLedger,
                                                  View,
                                                  currentUser
                                                ) ? (
                                                  <Button
                                                    type="button"
                                                    variant="primary"
                                                    className="uw-btn uw-btn-primary uw-btn-sm btn-table"
                                                    data-analytics-event="customers_customer_ledger_view_details_clicked"
                                                    onClick={() => {
                                                      setShowPermissionModal(
                                                        true
                                                      );
                                                    }}
                                                  >
                                                    View Details
                                                  </Button>
                                                ) : (
                                                  <></>
                                                )}
                                              </td>
                                            </tr>
                                          </React.Fragment>
                                        )
                                      )}
                                    </tbody>
                                  )}
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {totalRecords > 0 && customers?.length > 0 && (
          <OrderPagination
            totalRecords={totalRecords}
            currentPage={page}
            handleCurrentPage={handleCurrentPage}
            handleNextPage={handleNextPage}
            handlePreviousPage={handlePreviousPage}
            handlePageLimit={handlePageLimit}
            pageLimit={pageLimit}
          />
        )}
      </div>
    </>
  );
};

export default CustomerLedgerPage;
